import { useProposal } from '@/features/proposals/hooks/use-proposal.hook';
import type { Proposal } from '@/services/proposals/types';
import { useMemo } from 'react';
import type { ProposalAnalysisDetail } from '../types';

const PROPOSAL_VALIDITY_DAYS = 30;

function diffDaysFromToday(toDateIso: string | undefined): number {
  if (!toDateIso) return 0;
  const to = new Date(toDateIso);
  if (Number.isNaN(to.getTime())) return 0;
  const now = new Date();
  const diff = Math.ceil((to.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

function resolveDaysRemaining(proposal: Proposal): number {
  const baseDateIso = proposal.proposalDate || proposal.quotationDate;
  if (!baseDateIso) return 0;

  const base = new Date(baseDateIso);
  if (Number.isNaN(base.getTime())) return 0;

  const deadline = new Date(base);
  deadline.setDate(deadline.getDate() + PROPOSAL_VALIDITY_DAYS);
  return diffDaysFromToday(deadline.toISOString());
}

function buildAnalysisDetail(
  proposalId: string,
  proposal: Proposal
): ProposalAnalysisDetail {
  const customer = proposal.customer ?? ({} as Proposal['customer']);
  const proposalNumber =
    proposal.proposalNo || proposal.policyId || proposalId;

  return {
    id: proposalId,
    proposalNumber: String(proposalNumber),
    product: 'Empresarial',
    daysRemaining: resolveDaysRemaining(proposal),
    proponente: {
      cpf: customer.cpf ?? '',
      nome: customer.name ?? '',
      email: customer.email ?? '',
      telefone: customer.cellphone ?? customer.phone ?? '',
    },
    premium: {
      total: Number(proposal.grossPremium ?? proposal.DuePremium ?? 0) || 0,
      net: Number(proposal.rawPremium ?? proposal.netPremium ?? 0) || 0,
      iof: Number(proposal.iof ?? 0) || 0,
    },
    proposal,
  };
}

export function useProposalAnalysisDetail(proposalId: string) {
  const query = useProposal(proposalId);

  const detail = useMemo<ProposalAnalysisDetail | undefined>(() => {
    if (!query.data) return undefined;
    return buildAnalysisDetail(proposalId, query.data);
  }, [proposalId, query.data]);

  return {
    ...query,
    data: detail,
  };
}
